/**
 * AI Narrator Chat Component
 *
 * Interactive chat interface for the AI Narrator feature.
 * Allows users to ask questions about their security posture,
 * compliance status, and get contextual insights.
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  Card,
  Button,
  Input,
  Spinner,
  Text,
  Badge,
  Tooltip,
  Avatar,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components';
import {
  SendRegular,
  BotRegular,
  PersonRegular,
  ThumbLikeRegular,
  ThumbDislikeRegular,
  CopyRegular,
  MoreHorizontalRegular,
  ArchiveRegular,
  AddRegular,
  ChatRegular,
  SparkleRegular,
  WarningRegular,
} from '@fluentui/react-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { narratorApi } from '../../../services/api';
import type { ChatMessage, ContextType, ResponseRating } from '../../../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxHeight: '700px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  headerIcon: {
    fontSize: '24px',
    color: '#8852E0', // AI category color
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: tokens.spacingVerticalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  messageWrapper: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    maxWidth: '85%',
  },
  userMessageWrapper: {
    alignSelf: 'flex-end',
    flexDirection: 'row-reverse',
  },
  assistantMessageWrapper: {
    alignSelf: 'flex-start',
  },
  avatar: {
    flexShrink: 0,
  },
  messageContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  userMessage: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    borderTopRightRadius: tokens.borderRadiusSmall,
  },
  assistantMessage: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    borderTopLeftRadius: tokens.borderRadiusSmall,
  },
  messageTime: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  messageActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalXXS,
  },
  inputContainer: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  input: {
    flex: 1,
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    gap: tokens.spacingVerticalM,
  },
  emptyStateIcon: {
    fontSize: '48px',
    color: '#8852E0',
  },
  suggestionsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalM,
  },
  suggestionButton: {
    fontSize: tokens.fontSizeBase200,
  },
  loadingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    color: tokens.colorNeutralForeground3,
  },
  sessionSidebar: {
    width: '250px',
    borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
    display: 'flex',
    flexDirection: 'column',
  },
  sessionList: {
    flex: 1,
    overflowY: 'auto',
    padding: tokens.spacingVerticalS,
  },
  sessionItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusSmall,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  sessionItemActive: {
    backgroundColor: tokens.colorNeutralBackground3,
  },
  sessionTitle: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  chatLayout: {
    display: 'flex',
    height: '600px',
  },
  chatMain: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  citationBadge: {
    cursor: 'pointer',
    marginLeft: tokens.spacingHorizontalXXS,
  },
});

interface AINarratorChatProps {
  contextType?: ContextType;
  currentPage?: string;
  compact?: boolean;
}

const getErrorDisplayMessage = (error: string): string => {
  if (error === 'ai_account_attention_required') {
    return 'The AI Narrator service account requires attention. Please contact your administrator.';
  }
  if (error === 'ai_rate_limited') {
    return 'The AI Narrator is temporarily rate limited. Please try again in a moment.';
  }
  if (error === 'ai_authentication_error') {
    return 'The AI Narrator service account requires attention. Please contact your administrator.';
  }
  return 'AI Narrator is temporarily unavailable. Please try again later.';
};

const SUGGESTED_PROMPTS = [
  "Explain why a PR was blocked recently",
  "What are my top compliance coverage gaps?",
  "Show enforcement precision over the last 30 days",
  "Which controls are missing evidence?",
  "Summarize governance posture changes this week",
];

export const AINarratorChat: React.FC<AINarratorChatProps> = ({
  contextType = 'General',
  currentPage,
  compact = false,
}) => {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const [inputValue, setInputValue] = useState('');
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [showSidebar, setShowSidebar] = useState(!compact);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingContent, setStreamingContent] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Fetch sessions
  const { data: sessions, isLoading: sessionsLoading } = useQuery({
    queryKey: ['narrator-sessions'],
    queryFn: () => narratorApi.getSessions(),
  });

  // Fetch active session
  const { data: activeSession, isLoading: sessionLoading } = useQuery({
    queryKey: ['narrator-session', activeSessionId],
    queryFn: () => narratorApi.getSession(activeSessionId!),
    enabled: !!activeSessionId,
  });

  // Rate response mutation
  const rateResponseMutation = useMutation({
    mutationFn: ({ rating, feedback }: { rating: ResponseRating; feedback?: string }) =>
      narratorApi.rateResponse(activeSessionId!, rating, feedback),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['narrator-session', activeSessionId] });
    },
  });

  // Archive session mutation
  const archiveSessionMutation = useMutation({
    mutationFn: (sessionId: string) => narratorApi.archiveSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['narrator-sessions'] });
      if (activeSessionId) {
        setActiveSessionId(null);
      }
    },
  });

  // Auto-scroll to bottom when messages change or streaming content updates
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeSession?.messages, streamingContent]);

  const handleSendMessage = useCallback((overrideMessage?: string) => {
    const message = (overrideMessage ?? inputValue).trim();
    if (!message || isStreaming) return;
    setInputValue('');
    setIsStreaming(true);
    setStreamingContent('');
    setErrorMessage(null);

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    narratorApi.sendMessageStream(
      {
        sessionId: activeSessionId ?? undefined,
        message,
        contextType,
        currentPage,
      },
      {
        onSessionId: (sessionId) => {
          setActiveSessionId(sessionId);
        },
        onToken: (text) => {
          setStreamingContent((prev) => prev + text);
        },
        onDone: (meta) => {
          setIsStreaming(false);
          setStreamingContent('');
          setActiveSessionId(meta.sessionId);
          queryClient.invalidateQueries({ queryKey: ['narrator-sessions'] });
          queryClient.invalidateQueries({ queryKey: ['narrator-session', meta.sessionId] });
        },
        onError: (error) => {
          console.error('Narrator streaming error:', error);
          setIsStreaming(false);
          setStreamingContent('');
          setErrorMessage(getErrorDisplayMessage(error));
        },
      },
      abortController.signal,
    ).catch(() => {
      setIsStreaming(false);
      setStreamingContent('');
    });
  }, [inputValue, isStreaming, activeSessionId, contextType, currentPage, queryClient]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleNewChat = () => {
    setActiveSessionId(null);
  };

  const handleCopyMessage = (content: string) => {
    navigator.clipboard.writeText(content);
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  /** Strip [Platform Context] block from user messages for display */
  const stripPlatformContext = (content: string): string => {
    const idx = content.indexOf('\n\n[Platform Context]');
    return idx >= 0 ? content.substring(0, idx).trim() : content;
  };

  const renderMessage = (message: ChatMessage, index: number) => {
    const isUser = message.role === 'User';
    const isAssistant = message.role === 'Assistant';

    if (message.role === 'System') return null;

    const displayContent = isUser ? stripPlatformContext(message.content) : message.content;

    return (
      <div
        key={index}
        className={`${styles.messageWrapper} ${isUser ? styles.userMessageWrapper : styles.assistantMessageWrapper}`}
      >
        <Avatar
          className={styles.avatar}
          size={32}
          icon={isUser ? <PersonRegular /> : <BotRegular />}
          color={isUser ? 'brand' : 'neutral'}
        />
        <div className={styles.messageContent}>
          <div className={isUser ? styles.userMessage : styles.assistantMessage}>
            <Text>{displayContent}</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
            <Text className={styles.messageTime}>{formatTime(message.createdAt)}</Text>
            {isAssistant && (
              <div className={styles.messageActions}>
                <Tooltip content="Helpful" relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<ThumbLikeRegular />}
                    onClick={() => rateResponseMutation.mutate({ rating: 'Helpful' })}
                  />
                </Tooltip>
                <Tooltip content="Unhelpful" relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<ThumbDislikeRegular />}
                    onClick={() => rateResponseMutation.mutate({ rating: 'Unhelpful' })}
                  />
                </Tooltip>
                <Tooltip content="Copy" relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<CopyRegular />}
                    onClick={() => handleCopyMessage(message.content)}
                  />
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className={styles.emptyState}>
      <SparkleRegular className={styles.emptyStateIcon} />
      <Text size={500} weight="semibold">
        Ask the AI Narrator
      </Text>
      <Text style={{ color: tokens.colorNeutralForeground3, maxWidth: '400px' }}>
        Get insights about your security posture, enforcement status, and recommendations for improving your organization's security.
      </Text>
      <div className={styles.suggestionsContainer}>
        {SUGGESTED_PROMPTS.map((prompt, index) => (
          <Button
            key={index}
            appearance="outline"
            size="small"
            className={styles.suggestionButton}
            onClick={() => handleSendMessage(prompt)}
          >
            {prompt}
          </Button>
        ))}
      </div>
    </div>
  );

  const renderSessionSidebar = () => (
    <div className={styles.sessionSidebar}>
      <div style={{ padding: tokens.spacingVerticalM, borderBottom: `1px solid ${tokens.colorNeutralStroke1}` }}>
        <Button
          appearance="primary"
          icon={<AddRegular />}
          style={{ width: '100%' }}
          onClick={handleNewChat}
        >
          New Chat
        </Button>
      </div>
      <div className={styles.sessionList}>
        {sessionsLoading ? (
          <Spinner size="small" label="Loading..." />
        ) : sessions && sessions.length > 0 ? (
          sessions
            .filter((s) => s.status === 'Active')
            .map((session) => (
              <div
                key={session.id}
                className={`${styles.sessionItem} ${activeSessionId === session.id ? styles.sessionItemActive : ''}`}
                onClick={() => setActiveSessionId(session.id)}
              >
                <ChatRegular style={{ marginRight: tokens.spacingHorizontalS, flexShrink: 0 }} />
                <Text className={styles.sessionTitle} size={200}>
                  {session.title}
                </Text>
                <Menu>
                  <MenuTrigger disableButtonEnhancement>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<MoreHorizontalRegular />}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      <MenuItem
                        icon={<ArchiveRegular />}
                        onClick={(e) => {
                          e.stopPropagation();
                          archiveSessionMutation.mutate(session.id);
                        }}
                      >
                        Archive
                      </MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu>
              </div>
            ))
        ) : (
          <Text
            size={200}
            style={{ color: tokens.colorNeutralForeground3, padding: tokens.spacingVerticalM, textAlign: 'center' }}
          >
            No conversations yet
          </Text>
        )}
      </div>
    </div>
  );

  return (
    <Card className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <SparkleRegular className={styles.headerIcon} />
          <Text size={400} weight="semibold">
            AI Narrator
          </Text>
          {activeSession && (
            <Badge appearance="outline" size="medium">
              {activeSession.contextType}
            </Badge>
          )}
        </div>
        <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS }}>
          {!compact && (
            <Tooltip content={showSidebar ? 'Hide sessions' : 'Show sessions'} relationship="label">
              <Button
                appearance="subtle"
                icon={<ChatRegular />}
                onClick={() => setShowSidebar(!showSidebar)}
              />
            </Tooltip>
          )}
          <Tooltip content="New chat" relationship="label">
            <Button appearance="subtle" icon={<AddRegular />} onClick={handleNewChat} />
          </Tooltip>
        </div>
      </div>

      <div className={styles.chatLayout}>
        {showSidebar && renderSessionSidebar()}

        <div className={styles.chatMain}>
          <div className={styles.messagesContainer}>
            {sessionLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXL }}>
                <Spinner size="medium" label="Loading conversation..." />
              </div>
            ) : activeSession && activeSession.messages.length > 0 ? (
              <>
                {activeSession.messages.map((msg, idx) => renderMessage(msg, idx))}
                {isStreaming && !streamingContent && (
                  <div className={styles.loadingIndicator}>
                    <Spinner size="tiny" />
                    <Text size={200}>AI Narrator is thinking...</Text>
                  </div>
                )}
                {isStreaming && streamingContent && (
                  <div className={`${styles.messageWrapper} ${styles.assistantMessageWrapper}`}>
                    <Avatar className={styles.avatar} size={32} icon={<BotRegular />} color="neutral" />
                    <div className={styles.messageContent}>
                      <div className={styles.assistantMessage}>
                        <Text>{streamingContent}</Text>
                      </div>
                    </div>
                  </div>
                )}
                {errorMessage && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: tokens.spacingHorizontalS,
                      padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
                      backgroundColor: tokens.colorPaletteYellowBackground1,
                      borderRadius: tokens.borderRadiusMedium,
                      border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
                    }}
                  >
                    <WarningRegular style={{ color: tokens.colorPaletteYellowForeground1, fontSize: '20px', flexShrink: 0 }} />
                    <Text size={200} style={{ color: tokens.colorNeutralForeground1 }}>{errorMessage}</Text>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </>
            ) : (
              renderEmptyState()
            )}
          </div>

          <div className={styles.inputContainer}>
            <Input
              className={styles.input}
              placeholder="Ask about your security posture..."
              value={inputValue}
              onChange={(_, data) => setInputValue(data.value)}
              onKeyDown={handleKeyDown}
              disabled={isStreaming}
            />
            <Button
              appearance="primary"
              icon={<SendRegular />}
              onClick={() => handleSendMessage()}
              disabled={!inputValue.trim() || isStreaming}
            >
              Send
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};
